import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CyflarePeriodComponent } from '../../../../../core/components/cyflare-period/cyflare-period.component';
import { HudComponent } from './components/hud/hud.component';
import { AgentStatusComponent } from './components/agent-status/agent-status.component';
import { DetectedThreatStatusComponent } from './components/threat-status/threat-status.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChartData, AnalyticsService, WidgetData, CoverageData } from '../../../../../core/services/analytics.service';
import { BaseComponent } from '../../../../../core/shared/common/base.component';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { filter, take } from 'rxjs';

@Component({
  selector: 'app-edr',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CyflarePeriodComponent, HudComponent, AgentStatusComponent, DetectedThreatStatusComponent, RouterModule],
  templateUrl: './edr.component.html',
  styleUrl: './edr.component.scss',
  providers: [AnalyticsService],
})
export class EdrComponent extends BaseComponent {
  private route = inject(ActivatedRoute);
  private analyticsService = inject(AnalyticsService);

  // HUD-related signals
  public threats = signal<WidgetData>({ value: 0, colors: [] });
  public threatReboot = signal<WidgetData>({ value: 0, colors: [] });
  public outdatedAgents = signal<WidgetData>({ value: 0, colors: [] });
  public coverage = signal<CoverageData>({ value: [] });

  // Date-dependent signals
  public status = signal<ChartData[]>([]);
  public threatStatus = signal<ChartData[]>([]);

  public selectedDateRange = signal<string[]>([]);
  private dateRangeInternal = signal<string[]>([]);
  public organizationId = signal<number | null>(null);

  // Per-widget loading signals
  public threatsLoading = signal<boolean>(true);
  public threatRebootLoading = signal<boolean>(true);
  public outdatedAgentsLoading = signal<boolean>(true);
  public coverageLoading = signal<boolean>(true);
  public agentStatusLoading = signal<boolean>(false);
  public threatStatusLoading = signal<boolean>(false);

  constructor() {
    super();
    this.initInitialRouteHandler();
  }

  private initInitialRouteHandler() {
    this.route.queryParams
      .pipe(
        filter((params) => params['organizationId']),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (params) => {
          this.organizationId.set(+params['organizationId']);
          this.setHudData();

          const dateRange = this.selectedDateRange();
          if (dateRange?.length) {
            this.dateRangeInternal.set(dateRange);
            this.applyFilter(dateRange);
          }
        },
        error: (error) => {
          console.error('Error processing query parameters:', error);
        },
      });
  }

  // HUD data fetching - only called on init and refresh button click
  setHudData() {
    const organizationId = this.organizationId();
    if (!organizationId) return;

    this.threatsLoading.set(true);
    this.threatRebootLoading.set(true);
    this.outdatedAgentsLoading.set(true);
    this.coverageLoading.set(true);

    this.analyticsService
      .getOpenThreats(organizationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.threats.set(response);
          this.threatsLoading.set(false);
        },
        error: (error) => {
          console.error('Error fetching threats:', error);
          this.threatsLoading.set(false);
        },
      });
    this.analyticsService
      .getThreatRebootCount(organizationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.threatReboot.set(response);
          this.threatRebootLoading.set(false);
        },
        error: (error) => {
          console.error('Error fetching Threat Reboot count:', error);
          this.threatRebootLoading.set(false);
        },
      });
    this.analyticsService
      .getOutdatedAgents(organizationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.outdatedAgents.set(response);
          this.outdatedAgentsLoading.set(false);
        },
        error: (error) => {
          console.error('Error fetching Outdated Agents:', error);
          this.outdatedAgentsLoading.set(false);
        },
      });
    this.analyticsService
      .getCoverage(organizationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.coverage.set(response);
          this.coverageLoading.set(false);
        },
        error: (error) => {
          console.error('Error fetching Coverage:', error);
          this.coverageLoading.set(false);
        },
      });
  }

  // Date filter handling - only called when date range changes from cyflare-period
  applyFilter(dateRange: string[] | null) {
    const organizationId = this.organizationId();
    if (!dateRange?.length || !organizationId) return;

    this.dateRangeInternal.set(dateRange);

    const params = {
      timestamp__range: dateRange.join(','),
    };

    this.agentStatusLoading.set(true);
    this.threatStatusLoading.set(true);

    this.analyticsService
      .getAgentStatus(organizationId, params)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.status.set(response);
          this.agentStatusLoading.set(false);
        },
        error: (error) => {
          console.error('Error fetching Agent status:', error);
          this.agentStatusLoading.set(false);
        },
      });

    this.analyticsService
      .getDetectedThreatStatus(organizationId, params)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.threatStatus.set(response);
          this.threatStatusLoading.set(false);
        },
        error: (error) => {
          console.error('Error fetching detected threat status:', error);
          this.threatStatusLoading.set(false);
        },
      });
  }

  get dateRange(): string[] {
    return this.dateRangeInternal();
  }

  // HUD refresh handler - only called on refresh button click
  refreshHudData() {
    this.setHudData();
  }
}
